import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, notification, DatePicker, Space, message, Table, Tag, Card, Select } from "antd";
import jsPDF from 'jspdf';
import { Row, Form, Col } from "antd";
import { DownloadOutlined, FilterOutlined, ClearOutlined, SearchOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx';
import autoTable from 'jspdf-autotable';

import TopHeader from "../common/TopHeader/TopHeader";
import Sidebar from "../common/Sidebar/Sidebar";
// import FooterDashboard from "../common/FooterDashboard/FooterDashboard";
import dayjs from "dayjs";
import ReportsApi from "../common/Apis/ReportsApi";
import Pagination from "../common/Pagination/Pagination";
import "../common/Pagination/Pagination.css";

const ColdTraceabilityReport = () => {
    const { Content } = Layout;
    const [formAdd] = Form.useForm();
    const [coldTraceabilityData, setColdTraceabilityData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    
    // Filter states - Selected values
    const [batchCodeFilter, setBatchCodeFilter] = useState(null);
    const [storageLocationFilter, setStorageLocationFilter] = useState(null);
    const [recordedByFilter, setRecordedByFilter] = useState(null);

    // Filter options
    const [batchOptions, setBatchOptions] = useState([]);
    const [storageLocationOptions, setStorageLocationOptions] = useState([]);
    const [recordedByOptions, setRecordedByOptions] = useState([]);
    
    // Pagination state using your custom pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Check if any filter is applied
    const hasActiveFilters = batchCodeFilter || storageLocationFilter || recordedByFilter;

    // Display data - only show filteredData if filters are applied and there are results
    const displayData =
    filteredData.length > 0 || hasActiveFilters
        ? filteredData
        : coldTraceabilityData;

    const PageLoading = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    }, []);

    // Extract unique filter options from data
    const extractFilterOptions = useCallback((data) => {
        const batches = new Set();
        const locations = new Set();
        const recordedBy = new Set();

        data.forEach((item) => {
            if (item?.productionDTO?.batchCode) {
                batches.add(item.productionDTO.batchCode);
            }
            if (item?.storageDTO?.name) {
                locations.add(item.storageDTO.name);
            }
            if (item?.createdBy) {
                recordedBy.add(item.createdBy);
            }
        });

        setBatchOptions(Array.from(batches).sort());
        setStorageLocationOptions(Array.from(locations).sort());
        setRecordedByOptions(Array.from(recordedBy).sort());
    }, []);

    // Apply filters - uses applied filter states
    const applyFilters = useCallback((data) => {
    if (!data || data.length === 0) {
        setFilteredData([]);
        setTotalItems(0);
        setTotalPages(0);
        return;
    }

    let filtered = [...data];

    if (batchCodeFilter) {
        filtered = filtered.filter(
            item =>
                item?.productionDTO?.batchCode &&
                item.productionDTO.batchCode.toLowerCase() === batchCodeFilter.toLowerCase()
        );
    }

    if (storageLocationFilter) {
        filtered = filtered.filter(
            item =>
                item?.storageDTO?.name &&
                item.storageDTO.name.toLowerCase() === storageLocationFilter.toLowerCase()
        );
    }

    if (recordedByFilter) {
        filtered = filtered.filter(
            item =>
                item?.createdBy &&
                item.createdBy.toLowerCase() === recordedByFilter.toLowerCase()
        );
    }

    setFilteredData(filtered);
    setTotalItems(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
    setCurrentPage(1);
}, [batchCodeFilter, storageLocationFilter, recordedByFilter, pageSize]);

    // Update everything when coldTraceabilityData changes
    useEffect(() => {
        if (coldTraceabilityData.length > 0) {
            extractFilterOptions(coldTraceabilityData);
            applyFilters(coldTraceabilityData);
        } else {
            setFilteredData([]);
            setTotalItems(0);
            setTotalPages(0);
            setBatchOptions([]);
            setStorageLocationOptions([]);
            setRecordedByOptions([]);
        }
    }, [coldTraceabilityData, extractFilterOptions, applyFilters]);

    // Reset filters
    const resetFilters = () => {
    setBatchCodeFilter(null);
    setStorageLocationFilter(null);
    setRecordedByFilter(null);

    setFilteredData(coldTraceabilityData);
    setTotalItems(coldTraceabilityData.length);
    setTotalPages(Math.ceil(coldTraceabilityData.length / pageSize));
    setCurrentPage(1);

    message.info("All filters have been reset");
};

    const handleExportToPdf = () => {
        const dataToExport = displayData;
        
        if (!dataToExport || dataToExport.length === 0) {
            message.warning("No data available to export");
            return;
        }

        setExportLoading(true);

        try {
            const pdfData = [];

            dataToExport.forEach((item) => {
                let allergensString = '';

                if (item?.productionDTO?.allergens) {
                    const allergenList = Array.isArray(item.productionDTO.allergens)
                        ? item.productionDTO.allergens
                        : item.productionDTO.allergens.split(',');

                    allergensString = allergenList
                        .map(a => a.trim())
                        .join(', ');
                }

                let correctiveAction = item?.correctiveAction || '-';

                if (correctiveAction !== '-') {
                    if (correctiveAction.toLowerCase().includes('rework')) {
                        correctiveAction = 'Rework';
                    } else if (correctiveAction.toLowerCase().includes('reject')) {
                        correctiveAction = 'Rejected';
                    } else if (correctiveAction.toLowerCase().includes('approved')) {
                        correctiveAction = 'Approved';
                    }
                }

                pdfData.push([
                    formatDate(item.coldTraceabilityDate),
                    formatDateTime(item.coldTraceabilityTime),
                    formatDate(item?.productionDTO?.sellByDate),
                    item?.productionName || '-',
                    item?.productionDTO?.batchCode || '-',
                    item?.temp && item.temp !== 'N/A'
                        ? `${item.temp}°C`
                        : (item?.temp || '-'),
                    item?.storageDTO?.name || '-',
                    item?.storageDTO
                        ? `${item.storageDTO.temperatureMinInCelsius}°C to ${item.storageDTO.temperatureMaxInCelsius}°C`
                        : '-',
                    formatDate(item.oubd),
                    allergensString || '-',
                    `${(() => {
                        const value = Number(item?.productionDTO?.quantity || 0);

                        const normalStr = value.toLocaleString("fullwide", {
                            useGrouping: false,
                            maximumFractionDigits: 20,
                        });

                        const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);

                        return match ? match[0] : "0";
                    })()} ${item?.productionDTO?.unit || ''}`,
                    correctiveAction,
                    item?.createdBy || '-'
                ]);
            });

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            doc.setFontSize(16);
            doc.text('Cold Traceability Report', 14, 15);

            const startDate = formAdd.getFieldValue('startDate');
            const endDate = formAdd.getFieldValue('endDate');

            let filterInfo = '';
            if (startDate && endDate) {
                filterInfo += `Date Range: ${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`;
            }
            if (batchCodeFilter) {
                filterInfo += ` | Batch: ${batchCodeFilter}`;
            }
            if (storageLocationFilter) {
                filterInfo += ` | Storage: ${storageLocationFilter}`;
            }
            if (recordedByFilter) {
                filterInfo += ` | Recorded By: ${recordedByFilter}`;
            }
            filterInfo += ` | Total Records: ${dataToExport.length}`;

            doc.setFontSize(10);
            doc.text(filterInfo, 14, 22);

            autoTable(doc, {
                startY: 28,
                head: [[
                    'Date',
                    'Time',
                    'Sell By Date',
                    'Product Description',
                    'Batch Code',
                    'Temperature (°C)',
                    'Storage Location',
                    'Storage Temp Range',
                    'OUBD',
                    'Allergens',
                    'Quantity',
                    'Corrective Action',
                    'Recorded By'
                ]],
                body: pdfData,
                styles: {
                    fontSize: 7,
                    cellPadding: 2
                },
                headStyles: {
                    fillColor: [79, 129, 189],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold'
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                margin: {
                    left: 5,
                    right: 5
                },
                
                columnStyles: {
                0: { cellWidth: 16 }, 
                1: { cellWidth: 16 }, 
                2: { cellWidth: 18 }, 
                3: { cellWidth: 30 }, 
                4: { cellWidth: 30 }, 
                5: { cellWidth: 18 }, 
                6: { cellWidth: 22 }, 
                7: { cellWidth: 22 }, 
                8: { cellWidth: 16 },
                9: { cellWidth: 22 }, 
                10: { cellWidth: 16 }, 
                11: { cellWidth: 20 }, 
                12: { cellWidth: 18 }  
            }
            });

            let fileName = `Cold_Traceability_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`;

            if (startDate && endDate) {
                const startStr = dayjs(startDate).format('YYYY-MM-DD');
                const endStr = dayjs(endDate).format('YYYY-MM-DD');
                const batchStr = batchCodeFilter ? `_${batchCodeFilter}` : '';
                const storageStr = storageLocationFilter ? `_${storageLocationFilter}` : '';
                const recordedStr = recordedByFilter ? `_${recordedByFilter}` : '';
                fileName = `Cold_Traceability_Report_${startStr}_to_${endStr}${batchStr}${storageStr}${recordedStr}.pdf`;
            }

            doc.save(fileName);

            message.success("Cold traceability report exported successfully!");

        } catch (error) {
            console.error(error);
            message.error("Failed to export PDF");
        } finally {
            setExportLoading(false);
        }
    };

    const handlePrintButtonClick2 = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        const fontSize = 12;
        const margin = 5;
        const cellWidthFactor = 0.295;
        const cellHeightFactor = 0.2;

        doc.setFontSize(fontSize);

        const table = document.querySelector('.table');
        const rows = table.querySelectorAll('tr');

        const pageTitle = 'Cold Traceability Report';
        const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
        const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
        doc.text(pageTitleXPos, margin, pageTitle);

        doc.setFontSize(fontSize);
        const currentDate = new Date().toLocaleString();
        const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
        const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
        doc.text(dateTimeXPos, margin, currentDate);

        let yPos = margin + fontSize;

        rows.forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('th, td');
            let xPos = margin;

            cells.forEach((cell, cellIndex) => {
                const cellText = cell.textContent.trim();
                const cellWidth = cell.offsetWidth * cellWidthFactor;
                const cellHeight = row.offsetHeight * cellHeightFactor;

                if (yPos + cellHeight > doc.internal.pageSize.height - margin) {
                    doc.addPage();
                    yPos = margin + fontSize;
                    const pageTitle = 'Cold Traceability Report';
                    const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
                    const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
                    doc.text(pageTitleXPos, margin, pageTitle);

                    doc.setFontSize(fontSize);
                    const currentDate = new Date().toLocaleString();
                    const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
                    const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
                    doc.text(dateTimeXPos, margin, currentDate);
                }

                const isHeader = cell.tagName.toLowerCase() === 'th';
                const fontWeight = isHeader ? 'bold' : 'normal';

                doc.setFont(undefined, fontWeight);

                const textWidth = doc.getStringUnitWidth(cellText) * fontSize / doc.internal.scaleFactor;
                const cellPadding = 2;
                let xPosAlign = xPos + (cellWidth - textWidth - cellPadding) / 2;

                if (!isHeader) {
                    xPosAlign = xPos;
                }
                doc.rect(xPos, yPos, cellWidth, cellHeight);
                doc.text(xPosAlign + 1, yPos + 5, cellText);

                xPos += cellWidth;
            });

            yPos += row.offsetHeight * cellHeightFactor;
        });

        window.open(doc.output('bloburl'), '_blank');
    };

    const fetchColdTraceabilityData = useCallback(async (page = 1, pageSize = 10) => {
        const startDate = formAdd.getFieldValue('startDate');
        const endDate = formAdd.getFieldValue('endDate');
        
        if (!startDate || !endDate) {
            return;
        }
        
        try {
            PageLoading();
            const startDateStr = dayjs(startDate).format("DD/MM/YYYY");
            const endDateStr = dayjs(endDate).format("DD/MM/YYYY");
            const startTimestamp = convertToUnixTimestamp(startDateStr);
            const endTimestamp = convertToUnixTimestamp(endDateStr) + (24 * 60 * 60 * 1000);
            
            const response = await ReportsApi.ColdTraceabilityReport(startTimestamp, endTimestamp);
            
            let dataArray = [];
            let totalElements = 0;
            
            if (response?.payload) {
                dataArray = response.payload;
                totalElements = response.payload.length;
            } else if (response?.payloadDto?.content) {
                dataArray = response.payloadDto.content;
                totalElements = response.payloadDto.totalElements;
            } else if (Array.isArray(response)) {
                dataArray = response;
                totalElements = response.length;
            } else if (response?.content) {
                dataArray = response.content;
                totalElements = response.totalElements;
            }
            
            setColdTraceabilityData(dataArray);
            // Reset filters when new data is loaded
            setBatchCodeFilter(null);
            setStorageLocationFilter(null);
            setFilteredData(dataArray);
            setRecordedByFilter(null);
            setTotalItems(totalElements);
            setTotalPages(Math.ceil(totalElements / pageSize));
            setCurrentPage(page);
            setPageSize(pageSize);
        } catch (err) {
            console.log(err);
            if (err.apiError?.status) {
                notification.open({
                    message: (
                        <span style={{ fontWeight: "bold" }}>{err.apiError.status}</span>
                    ),
                    description: err.apiError.message,
                    closeIcon: false,
                    duration: 3,
                    style: {
                        backgroundColor: "#fff2f0",
                        color: "red",
                    },
                });
            } else {
                notification.error({
                    message: "Error",
                    description: err?.message || "Failed to fetch cold traceability report",
                });
            }
        }
    }, [PageLoading]);

    const handleFilter = (data) => {
        setFilterLoading(true);
        fetchColdTraceabilityData(1, pageSize);
        setTimeout(() => {
            setFilterLoading(false);
        }, 800);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    function formatDate(timestamp) {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return '-';
        const options = { day: "2-digit", month: "short", year: "numeric" };
        return date.toLocaleDateString("en-US", options);
    }

    function formatDateTime(timestamp) {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return '-';
        const options = { day: "2-digit", month: "short", year: "numeric", hour: '2-digit', minute: '2-digit' };
        return date.toLocaleDateString("en-US", options);
    }

    function convertToUnixTimestamp(dateString) {
        const parts = dateString.split("/");
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const unixTimestamp = Date.UTC(year, month, day);
        return unixTimestamp;
    }

    const handleClear = () => {
        setIsLoading(true);
        setColdTraceabilityData([]);
        setFilteredData([]);
        formAdd.resetFields();
        // Reset filters
        setBatchCodeFilter(null);
        setStorageLocationFilter(null);
        setRecordedByFilter(null);
        const tomorrow = dayjs().add(1, 'day');
        const today = dayjs();
        formAdd.setFieldsValue({
            startDate: today,
            endDate: tomorrow,
        });
        setTimeout(() => {
            setIsLoading(false);
            fetchColdTraceabilityData(1, 10);
        }, 800);
    };

    const getAllergensTag = (allergens) => {
        if (!allergens) return '-';
        const allergenList = Array.isArray(allergens) ? allergens : allergens.split(',');
        return (
            <Space size={[0, 4]} wrap>
                {allergenList.map((allergen, index) => (
                    <Tag key={index} color="orange">{allergen.trim()}</Tag>
                ))}
            </Space>
        );
    };

    const getCorrectiveActionTag = (correctiveAction) => {
        if (!correctiveAction) return '-';
        if (correctiveAction.toLowerCase().includes('rework')) {
            return <Tag color="orange">Rework</Tag>;
        } else if (correctiveAction.toLowerCase().includes('reject')) {
            return <Tag color="red">Rejected</Tag>;
        } else if (correctiveAction.toLowerCase().includes('approved')) {
            return <Tag color="green">Approved</Tag>;
        }
        return <Tag color="blue">{correctiveAction}</Tag>;
    };

    // Get current page data for client-side pagination
    const getCurrentPageData = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return displayData.slice(startIndex, endIndex);
    };

    const currentPageData = getCurrentPageData();

    useEffect(() => {
        const tomorrow = dayjs().add(1, 'day');
        const today = dayjs();
        
        formAdd.setFieldsValue({
            startDate: today,
            endDate: tomorrow,
        });
        
        fetchColdTraceabilityData(1, 10);
    }, []);

    const columns = [
        {
            title: 'Date',
            dataIndex: 'coldTraceabilityDate',
            key: 'coldTraceabilityDate',
            width: '10%',
            render: (date) => formatDate(date),
        },
        {
            title: 'Time',
            dataIndex: 'coldTraceabilityTime',
            key: 'coldTraceabilityTime',
            width: '10%',
            render: (time) => formatDateTime(time),
        },
        {
            title: 'Sell By Date',
            dataIndex: ['productionDTO', 'sellByDate'],
            key: 'sellByDate',
            width: '10%',
            render: (date) => formatDate(date),
        },
        {
            title: 'Product Description',
            dataIndex: 'productionName',
            key: 'productionName',
            width: '12%',
            render: (text) => text || '-',
        },
        {
            title: 'Batch Code',
            dataIndex: ['productionDTO', 'batchCode'],
            key: 'batchCode',
            width: '12%',
            render: (code) => {
                if (!code) return '-';
                return (
                    <div style={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        whiteSpace: 'nowrap',
                        maxWidth: '100px',
                        padding: '0px 0px 20px',
                        display: 'block',
                    }}>
                        {code}
                    </div>
                );
            },
        },
        {
            title: 'Temperature (°C)',
            dataIndex: 'temp',
            key: 'temp',
            width: '8%',
            render: (temp) => temp && temp !== 'N/A' ? `${temp}°C` : (temp || '-'),
        },
        {
            title: 'Storage Location',
            dataIndex: ['storageDTO', 'name'],
            key: 'storageName',
            width: '12%',
            render: (name) => name || '-',
        },
        {
            title: 'Storage Temp Range',
            dataIndex: ['storageDTO'],
            key: 'storageTempRange',
            width: '12%',
            render: (storageDTO) => {
                if (!storageDTO) return '-';
                return `${storageDTO.temperatureMinInCelsius}°C to ${storageDTO.temperatureMaxInCelsius}°C`;
            },
        },
        {
            title: 'OUBD',
            dataIndex: 'oubd',
            key: 'oubd',
            width: '10%',
            render: (date) => formatDate(date),
        },
        {
            title: 'Allergens',
            dataIndex: ['productionDTO', 'allergens'],
            key: 'allergens',
            width: '10%',
            render: (allergens) => getAllergensTag(allergens),
        },
        {
            title: 'Quantity',
            dataIndex: ['productionDTO', 'quantity'],
            key: 'quantity',
            width: '8%',
            align: 'right',
            render: (qty, record) => {
                const value = Number(qty || 0);

                const normalStr = value.toLocaleString("fullwide", {
                    useGrouping: false,
                    maximumFractionDigits: 20,
                });

                const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);

                return `${match ? match[0] : "0"} ${record?.productDTOs?.[0]?.unitUomOne || ""}`;
                },
        },
        {
            title: 'Corrective Action',
            dataIndex: 'correctiveAction',
            key: 'correctiveAction',
            width: '10%',
            render: (action) => getCorrectiveActionTag(action),
        },
        {
            title: 'Recorded By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: '10%',
            render: (name) => name || '-',
        },
    ];

    const centeredColumns = columns.map(col => ({
        ...col,
        align: 'center',
    }));

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout>
                <TopHeader onPrintResponse2={handlePrintButtonClick2} />
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}

                <Content
                    className="form-content nav-content"
                    style={{
                        margin: "14px 16px 0",
                        padding: "30px",
                        background: "white",
                    }}
                >
                    <div>
                        {/* Filter Section - Separated Card */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <FilterOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Date Range Filter</span>
                                </Space>
                            </div>
                            
                            <Form
                                form={formAdd}
                                name="cold_traceability_filter_form"
                                layout="vertical"
                                onFinish={handleFilter}
                            >
                                <Row gutter={[16, 0]}>
                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="startDate"
                                            label="Start Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select Start Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select start date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="endDate"
                                            label="End Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select End Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select end date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={24} sm={24} xs={24}>
                                        <Form.Item label="Actions">
                                            <Space size="middle" wrap>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={filterLoading}
                                                    icon={<FilterOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Apply Date Filter
                                                </Button>
                                                <Button
                                                    onClick={() => handleClear()}
                                                    icon={<ClearOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Clear All
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>

                        {/* Advanced Filters Section */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                background: '#fafafa'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <SearchOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Advanced Filters</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        (Filter by Batch Code, Storage Location, Recorded By)
                                    </span>
                                </Space>
                            </div>
                            
                            <Row gutter={[16, 16]}>
                                <Col lg={8} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Batch Code
                                    </div>
                                    <Select
                                        placeholder="All Batches"
                                        style={{ width: '100%' }}
                                        value={batchCodeFilter}
                                        onChange={(value) => setBatchCodeFilter(value)}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {batchOptions.map(batch => (
                                            <Select.Option key={batch} value={batch}>{batch}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col lg={8} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Storage Location
                                    </div>
                                    <Select
                                        placeholder="All Locations"
                                        style={{ width: '100%' }}
                                        value={storageLocationFilter}
                                        onChange={(value) => setStorageLocationFilter(value)}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {storageLocationOptions.map(location => (
                                            <Select.Option key={location} value={location}>{location}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col lg={8} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Recorded By
                                    </div>
                                    <Select
                                        placeholder="All Recorded By"
                                        style={{ width: '100%' }}
                                        value={recordedByFilter}
                                        onChange={(value) => setRecordedByFilter(value)}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {recordedByOptions.map(name => (
                                            <Select.Option key={name} value={name}>{name}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>
                            </Row>
                            
                            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                <Space>
                                  
                                    <Button
                                        onClick={resetFilters}
                                        icon={<ClearOutlined />}
                                        style={{ height: '36px' }}
                                        disabled={!hasActiveFilters}
                                    >
                                        Reset Filters
                                    </Button>
                                </Space>
                                <div style={{ padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', fontSize: '12px', color: '#1890ff' }}>
                                    <strong>Total Records:</strong> {coldTraceabilityData.length} | 
                                    <strong> Displaying:</strong> {displayData.length} records
                                    {hasActiveFilters && (
                                        <span style={{ marginLeft: '8px' }}>
                                            (Filtered by: 
                                            {batchCodeFilter && ` Batch: ${batchCodeFilter}`}
                                            {storageLocationFilter && `, Storage: ${storageLocationFilter}`}
                                            {recordedByFilter && `, Recorded By: ${recordedByFilter}`}
                                            )
                                        </span>
                                    )}
                                    {hasActiveFilters && displayData.length === 0 && (
                                        <span style={{ color: '#ff4d4f', marginLeft: '8px' }}>
                                            ⚠️ No records match the selected filters
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Card>

                        {/* Report Results Section */}
                        <Card 
                            style={{ 
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ 
                                marginBottom: '16px', 
                                borderBottom: '1px solid #f0f0f0', 
                                paddingBottom: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <Space>
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Cold Traceability Report Results</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        {displayData.length > 0 && `(${displayData.length} records found)`}
                                        {hasActiveFilters && displayData.length === 0 && 
                                            ` (No matching records)`
                                        }
                                    </span>
                                </Space>
                                <Button
                                    type="primary"
                                    onClick={handleExportToPdf}
                                    loading={exportLoading}
                                    icon={<DownloadOutlined />}
                                    style={{ 
                                        height: '36px',
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a'
                                    }}
                                    disabled={displayData.length === 0}
                                >
                                    Export Cold Traceability Report PDF
                                </Button>
                            </div>
                            
                            <div className="dateFilterTable" style={{ overflowX: 'auto' }}>
                                <Table
                                    columns={centeredColumns}
                                    dataSource={currentPageData}
                                    rowKey="id"
                                    pagination={false}
                                    bordered
                                    className="users-table Reportable"
                                    scroll={{ x: 'max-content' }}
                                    locale={{
                                        emptyText: hasActiveFilters ? 'No records match the selected filters' : 'No data available'
                                    }}
                                />
                            </div>
                            
                            {/* Custom Pagination - Only show if there are records */}
                            {displayData.length > 0 && (
                                <div style={{ marginTop: '20px' }}>
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        maxVisiblePages={5}
                                        showPageInfo={true}
                                        totalItems={totalItems}
                                        pageSize={pageSize}
                                    />
                                </div>
                            )}
                        </Card>
                    </div>
                </Content>

                {/* <FooterDashboard /> */}
            </Layout>
        </Layout>
    );
};

export default ColdTraceabilityReport;